/**
 * Bridge Service
 *
 * Handles communication and matching between residents and vendors
 */

// Basic type definitions for the bridge service
interface RequestSubmissionResult {
  success: boolean;
  requestId?: string;
  message: string;
  error?: string;
}

interface ServiceRequestData {
  title: string;
  description: string;
  category: string;
  priority?: string;
  location?: string;
  budget?: number;
  timeline?: string;
  attachments?: string[];
}

class BridgeService {
  private baseUrl = '/api/bridge';
  private _realtimeStarted = false;
  private _intervals: number[] = [];

  // Basic service request submission
  async submitServiceRequest(request: ServiceRequestData): Promise<RequestSubmissionResult> {
    try {
      const response = await fetch(`${this.baseUrl}/requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...request,
          status: 'posted',
          quotes: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          message: data.message || 'Failed to submit service request',
          error: data.error,
        };
      }

      return {
        success: true,
        requestId: data.requestId,
        message: 'Service request submitted successfully',
      };
    } catch (error) {
      console.error('Error submitting service request:', error);
      return {
        success: false,
        message: 'Failed to submit service request',
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  // Real-time subscriptions
  private listeners: Map<string, Set<(data: unknown) => void>> = new Map();

  subscribe(event: string, callback: (data: unknown) => void): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }

    this.listeners.get(event)!.add(callback);

    return () => {
      const eventListeners = this.listeners.get(event);
      if (eventListeners) {
        eventListeners.delete(callback);
        if (eventListeners.size === 0) {
          this.listeners.delete(event);
        }
      }
    };
  }

  private emit(event: string, data: unknown): void {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.forEach((callback) => callback(data));
    }
  }

  // Mock real-time events for development
  public simulateRealTimeEvents(): void {
    if (this._realtimeStarted) return;
    this._realtimeStarted = true;

    // Simulate new quotes
    const quotes = window.setInterval(() => {
      this.emit('new_quote', {
        quoteId: `quote-${Date.now()}`,
        vendorName: 'ABC Services',
        amount: Math.floor(Math.random() * 1000) + 100,
      });
    }, 45000); // Every 45 seconds
    this._intervals.push(quotes);

    // Simulate new messages
    const messages = window.setInterval(() => {
      this.emit('new_message', {
        conversationId: `conv-${Date.now()}`,
        senderName: 'John Vendor',
        preview: 'I have a question about...',
      });
    }, 20000); // Every 20 seconds
    this._intervals.push(messages);
  }

  public stopRealTimeEvents(): void {
    this._intervals.forEach((id) => clearInterval(id));
    this._intervals = [];
    this._realtimeStarted = false;
  }
}

// Create singleton instance
export const bridgeService = new BridgeService();

// Start simulating real-time events in development
if (import.meta.env.DEV) {
  bridgeService.simulateRealTimeEvents();
  if (import.meta.hot) {
    import.meta.hot.dispose(() => {
      bridgeService.stopRealTimeEvents();
    });
  }
}
